import { Divider, Stack, Typography } from '@mui/material'
import { Fragment, useMemo } from 'react'

import {
  ConsolePreSaleDetailFragment,
  PaymentMethod,
} from '@/clients/utoniq-core/schema'
import { useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'
import { filterHiddenItem } from '@/lib/array'

type PreSaleContentCellProps = {
  preSale: ConsolePreSaleDetailFragment
}

export const PreSaleContentCell: Cell<PreSaleContentCellProps> = ({
  preSale,
}) => {
  const { t } = useTranslation('consolePreSaleDetail')

  const labels = useMemo(
    () =>
      filterHiddenItem([
        {
          title: t('userApply', '応募可能なユーザー'),
          content:
            preSale.isPremium ||
            (preSale.purchesablePlanIds &&
              preSale.purchesablePlanIds?.length > 0)
              ? t('membersOnly')
              : t('all'),
        },
        {
          title: t('maximumDesiredNumber', '最大希望数'),
          content: preSale.lottery?.maxChoice ?? 0,
          hidden: !preSale.lottery,
        },
        {
          title: t('maximumAppNumber', '応募枚数上限'),
          content: preSale.lottery?.purchasableCount ?? 0,
          hidden: !preSale.lottery,
        },
        {
          title: t('maximumAppNumber', '応募枚数上限'),
          content: preSale.firstComeFirstServed?.purchasableCount ?? 0,
          hidden: !preSale.firstComeFirstServed,
        },
      ]),
    [preSale.firstComeFirstServed, preSale.lottery, t]
  )

  const preSaleInfos = useMemo(
    () =>
      filterHiddenItem([
        {
          title: t('applicationDate', '先行抽選受付期間'),
          content: `${preSale?.lottery?.formattedApplicationStartedAt} 〜 ${preSale?.lottery?.formattedApplicationEndedAt}`,
          hidden: !preSale.lottery,
        },
        {
          title: t('resultNoticeDate', '当落発表予定日'),
          content: preSale?.lottery?.formattedResultNoticedAt,
          hidden: !preSale?.lottery?.formattedResultNoticedAt,
        },
        {
          title: t('paymentDate', '決済期間'),
          content: `${preSale?.lottery?.formattedPurchasableStartedAt} 〜 ${preSale?.lottery?.formattedPurchasableEndedAt}`,
          hidden: !preSale.lottery,
        },
        {
          title: t('purchasableDate', '購入期間'),
          content: `${preSale?.firstComeFirstServed?.formattedPurchasableStartedAt} 〜 ${preSale?.firstComeFirstServed?.formattedPurchasableEndedAt}`,
          hidden: !preSale.firstComeFirstServed,
        },
        {
          title: t('paymentMethod', '決済方法'),
          content: (
            preSale?.paymentMethods ?? [
              PaymentMethod.CreditCard,
              PaymentMethod.Konbini,
            ]
          )
            .map(paymentMethod => t(paymentMethod))
            .join('/'),
        },
        {
          title: t('note', '注意事項'),
          content: preSale.note,
        },
      ]),
    [t, preSale]
  )

  return (
    <CellFrame sx={{ pt: 3, pb: 5 }}>
      <Stack direction="row" alignItems="center" spacing={4} sx={{ pb: 4 }}>
        {labels.map(({ title, content }, index) => (
          <Fragment key={title}>
            <Stack spacing={1}>
              <Typography variant="overline" color="text.hint">
                {title}
              </Typography>
              <Typography variant="subtitle2">{content}</Typography>
            </Stack>
            {labels.length - 1 > index && (
              <Divider orientation="vertical" flexItem />
            )}
          </Fragment>
        ))}
      </Stack>
      <Typography variant="body2">{preSale.description}</Typography>
      {preSaleInfos.map((info, index) => (
        <Stack sx={{ py: 2 }} key={index} spacing={1}>
          <Typography variant="subtitle1" color="text.secondary">
            {info.title}
          </Typography>
          <Typography variant="body2">{info.content}</Typography>
        </Stack>
      ))}
    </CellFrame>
  )
}
